'use client';

import type { ListProduct, WishProduct } from '@/api/types/public';
import Image from '@/components/Image';
import Wish from '@/components/Product/Wish';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import { KeyboardEvent, RefObject, memo, useEffect, useMemo, useState } from 'react';
import { useValidExposure } from '@/hooks/useValidExposure';
import { getStyleHotProductColorStateKey } from '../../hooks/styleHotProductViewState';
import type { StyleHotProductCategoryProduct } from '../../types';

const FALLBACK_CURRENCY = {
  name: 'USD',
  full_symbol: '$',
  symbol: '$',
  exchange_rate: '1.000000',
  symbol_prefix: '$',
  symbol_suffix: '',
} as const;

interface StyleProductListWapProps {
  products?: StyleHotProductCategoryProduct[];
  tabName?: string;
  firstRowRef?: RefObject<HTMLDivElement | null>;
  isLoading?: boolean;
  selectedColorByProductKey?: Record<string, number>;
  onProductClick?: (item: StyleHotProductCategoryProduct, index: number) => void | Promise<void>;
  onProductColorChange?: (item: StyleHotProductCategoryProduct, colorIndex: number) => void;
  onProductExposure?: (item: StyleHotProductCategoryProduct, index: number) => void;
}

interface StyleProductCardWapProps {
  product: StyleHotProductCategoryProduct;
  variant: 'large' | 'small';
  index: number;
  initialColorIndex?: number;
  onProductClick?: (item: StyleHotProductCategoryProduct, index: number) => void | Promise<void>;
  onProductColorChange?: (item: StyleHotProductCategoryProduct, colorIndex: number) => void;
}

type WishProductData = (ListProduct | WishProduct) & {
  business_sku?: string;
  sku?: string;
};

type StyleProductColorItem = NonNullable<StyleHotProductCategoryProduct['correlation']>[number];
type StyleProductImageKind = 'product' | 'product_gray' | 'celebrity' | 'material' | 'other';

interface StyleProductImageInfo {
  src: string;
  kind: StyleProductImageKind;
}

function mergeCorrelationProduct(
  product: StyleHotProductCategoryProduct,
  correlationItem?: StyleProductColorItem,
): StyleHotProductCategoryProduct {
  if (!correlationItem) {
    return product;
  }

  return {
    ...product,
    ...correlationItem,
    pc_image_type: product.pc_image_type,
    wap_image_type: product.wap_image_type,
    correlation: product.correlation,
    fine_ranking_type: product.fine_ranking_type,
    recall_group_type: product.recall_group_type,
  } as unknown as StyleHotProductCategoryProduct;
}

function getImageByType(product: StyleHotProductCategoryProduct): StyleProductImageInfo {
  const imageMap = (product.image || {}) as Record<string, string[] | undefined>;
  const imageType = product.wap_image_type || product.pc_image_type || 'product';
  const candidates: Array<[StyleProductImageKind, string[] | undefined]> = [
    [imageType as StyleProductImageKind, imageMap[imageType]],
    ['product_gray', imageMap.product_gray],
    ['product', imageMap.product],
  ];
  const matchedImage = candidates.find(([, images]) => images?.[0]);

  if (!matchedImage) {
    return { src: '', kind: 'other' };
  }

  const [kind, images] = matchedImage;

  return {
    src: images?.[0] || '',
    kind,
  };
}

function getProductName(product: StyleHotProductCategoryProduct): string {
  return product.short_name || product.name || product.shop_sku || '';
}

function getStyleProductRowStartIndex(productIndex: number): number {
  return productIndex <= 0 ? 0 : 1 + Math.floor((productIndex - 1) / 2) * 2;
}

function isSoldOutProduct(product: StyleHotProductCategoryProduct): boolean {
  const stock = Number(product.stock);

  return !Number.isNaN(stock) && stock <= 0;
}

function isOffShelfProduct(product: StyleHotProductCategoryProduct): boolean {
  const status = Number(product.status);

  return !Number.isNaN(status) && status !== 1;
}

function normalizeProductList(
  products?: StyleHotProductCategoryProduct[],
): StyleHotProductCategoryProduct[] {
  return (Array.isArray(products) ? products : [])
    .filter((product) => product?.id && !isOffShelfProduct(product))
    .map((product, index) => ({ product, index }))
    .sort((a, b) => {
      const soldOutDiff = Number(isSoldOutProduct(a.product)) - Number(isSoldOutProduct(b.product));

      return soldOutDiff || a.index - b.index;
    })
    .map(({ product }) => product);
}

function StyleProductColorDotsWap({
  product,
  selectedIndex,
  onColorClick,
}: {
  product: StyleHotProductCategoryProduct;
  selectedIndex: number;
  onColorClick: (index: number) => void;
}) {
  const correlations = Array.isArray(product.correlation) ? product.correlation : [];
  const visibleColors = correlations.slice(0, 3);
  const displayColorNum = Number(
    (product as Record<string, unknown>).display_color_num || correlations.length || 0,
  );
  const restCount = Math.max(displayColorNum - visibleColors.length, 0);

  if (visibleColors.length === 0 && restCount === 0) {
    return null;
  }

  return (
    <div className="mt-[8px] flex h-[20px] items-center gap-[8px]">
      {visibleColors.map((item, index) => {
        const colorImage = (item as Record<string, string | undefined>)
          .product_frame_color_main_url;
        const colorValue =
          (item as Record<string, string | undefined>).product_frame_color ||
          (item as Record<string, string | undefined>).color;
        const colorName =
          (item as Record<string, string | undefined>).product_frame_color_name || '';
        const isSelected = index === selectedIndex;

        return (
          <span
            className="flex h-[20px] w-[20px] items-center justify-center"
            key={item.id || `${colorImage || colorValue || index}-${index}`}
          >
            <span
              role="radio"
              aria-checked={isSelected}
              aria-label={colorName}
              tabIndex={0}
              className={`flex h-[20px] w-[20px] cursor-pointer items-center justify-center rounded-full border border-transparent ${
                isSelected ? 'border-primary-purple-active' : ''
              }`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onColorClick(index);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  onColorClick(index);
                }
              }}
            >
              <span
                className="block h-[16px] w-[16px] rounded-full border border-white bg-[#d8d8d8] shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
                style={{
                  backgroundColor: colorImage ? undefined : colorValue || '#d8d8d8',
                  backgroundImage: colorImage ? `url(${colorImage})` : undefined,
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                }}
              />
            </span>
          </span>
        );
      })}
      {restCount > 0 && (
        <span className="font-br text-[12px] leading-[14px] text-[#808080]">+{restCount}</span>
      )}
    </div>
  );
}

const ExposureDetector = memo(function ExposureDetector({
  item,
  index,
  onExposure,
}: {
  item: StyleHotProductCategoryProduct;
  index: number;
  onExposure?: (item: StyleHotProductCategoryProduct, index: number) => void;
}) {
  const { ref } = useValidExposure({
    callback: () => item.id && onExposure?.(item, index),
    ts: item.id,
  });

  return <div ref={ref} className="pointer-events-none absolute inset-0" />;
});

function StyleProductCardWap({
  product,
  variant,
  index,
  initialColorIndex = 0,
  onProductClick,
  onProductColorChange,
}: StyleProductCardWapProps) {
  const { navigate } = useAppNavigation();
  const t = useTranslations('product');
  const currency = useStore((state) => state.currency) || FALLBACK_CURRENCY;
  const userInfo = useStore((state) => state.userInfo);
  const correlations = useMemo(
    () => (Array.isArray(product.correlation) ? product.correlation : []),
    [product.correlation],
  );
  const [selectedColorIndex, setSelectedColorIndex] = useState(initialColorIndex);
  const selectedProduct = useMemo(
    () => mergeCorrelationProduct(product, correlations[selectedColorIndex]),
    [correlations, product, selectedColorIndex],
  );
  const productImage = getImageByType(selectedProduct);
  const productName = getProductName(selectedProduct);
  const isSoldOut = isSoldOutProduct(selectedProduct);
  const priceData = getPriceInfo(userInfo, selectedProduct, currency as never) as {
    price?: string;
    sell_price?: string;
  };
  const sellPrice = priceData.sell_price || priceData.price || '';
  const originalPrice = priceData.price || '';
  const isLarge = variant === 'large';
  const imageShouldCover = productImage.kind === 'celebrity' || productImage.kind === 'material';
  const imageAreaClassName = isLarge ? 'h-[209px]' : 'h-[172px]';
  const cardClassName = isLarge ? 'h-[297px]' : 'h-[251px]';
  const productImagePaddingClassName = isLarge ? 'px-[30px] py-[32px]' : 'px-[11px] py-[48px]';
  const containImageWidth = isLarge ? 291 : 150;
  const containImageHeight = isLarge ? 145 : 75;

  useEffect(() => {
    setSelectedColorIndex(initialColorIndex);
  }, [initialColorIndex, product.id]);

  const handleCardClick = async () => {
    await onProductClick?.(selectedProduct, index);

    if (selectedProduct.shop_sku) {
      navigate(`/goods-detail/${selectedProduct.shop_sku}`);
    }
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void handleCardClick();
    }
  };

  return (
    <article className={`relative overflow-hidden rounded-[8px] bg-[#eeeeee] ${cardClassName}`}>
      <div
        role="link"
        tabIndex={0}
        className="block h-full w-full cursor-pointer"
        aria-label={productName}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
      >
        <div
          className={`relative w-full rounded-t-[8px] bg-[#eeeeee] ${imageAreaClassName} ${
            imageShouldCover
              ? ''
              : `flex items-center justify-center ${productImagePaddingClassName}`
          }`}
        >
          {productImage.src && imageShouldCover && (
            <Image
              src={productImage.src}
              alt={productName}
              fill
              sizes={isLarge ? '351px' : '172px'}
              className={`h-full w-full object-cover object-center ${isSoldOut ? 'opacity-50' : ''}`}
            />
          )}
          {productImage.src && !imageShouldCover && (
            <Image
              src={productImage.src}
              alt={productName}
              width={containImageWidth}
              height={containImageHeight}
              sizes={isLarge ? '300px' : '132px'}
              className={`h-auto max-h-full w-full object-contain ${
                productImage.kind === 'product' ? 'mix-blend-multiply' : ''
              } ${isSoldOut ? 'opacity-50' : ''}`}
            />
          )}
        </div>

        <div
          className={`rounded-b-[8px] bg-[#eeeeee] ${
            isLarge ? 'h-[88px] px-[16px] pb-[12px] pt-[12px]' : 'px-[8px] pb-[8px] pt-[8px]'
          }`}
        >
          <p className="line-clamp-1 font-br text-[12px] leading-[14px] text-[#282828]">
            {productName}
          </p>
          <div className="mt-[4px] flex h-[17px] items-center gap-[4px]">
            {sellPrice && (
              <span className="notranslate font-bb text-[14px] leading-[17px] text-[#282828]">
                {formatPriceNumberWithSymbol(sellPrice, currency as never)}
              </span>
            )}
            {originalPrice && originalPrice !== sellPrice && (
              <span className="notranslate font-br text-[14px] leading-[17px] text-[#808080] line-through">
                {formatPriceNumberWithSymbol(originalPrice, currency as never)}
              </span>
            )}
          </div>
          <StyleProductColorDotsWap
            product={product}
            selectedIndex={selectedColorIndex}
            onColorClick={(colorIndex) => {
              setSelectedColorIndex(colorIndex);
              onProductColorChange?.(product, colorIndex);
            }}
          />
        </div>
      </div>

      <Wish
        data={selectedProduct as unknown as WishProductData}
        className="absolute right-[10px] top-[10px] z-[3] leading-[18px] text-auxiliary-dark-gray"
        iconClassName="text-[18px]"
        iconSize="18px"
        showWishNum={false}
        loginSituation="周年庆风格爆品收藏"
        collectScene="周年庆风格爆品"
        price={sellPrice}
      />

      {isSoldOut && (
        <span
          className={`pointer-events-none absolute left-1/2 z-[2] flex h-[66px] w-[66px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-center ${
            isLarge ? 'top-[104px]' : 'top-[86px]'
          }`}
        >
          <span
            className="font-bs text-[11px] leading-[normal] text-white"
            dangerouslySetInnerHTML={{ __html: t('soldOut.soldOut') }}
          />
        </span>
      )}
    </article>
  );
}

function StyleProductSkeletonListWap({
  firstRowRef,
}: Pick<StyleProductListWapProps, 'firstRowRef'>) {
  return (
    <div className="w-full px-[12px]" aria-busy="true">
      <div
        ref={firstRowRef}
        className="h-[297px] w-full animate-pulse rounded-[8px] bg-[#eeeeee]/70"
      />
      <div className="mt-[8px] grid w-full grid-cols-2 gap-x-[7px] gap-y-[8px]">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            className="h-[251px] w-full animate-pulse rounded-[8px] bg-[#eeeeee]/60"
            key={`style-product-skeleton-wap-${index}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function StyleProductListWap({
  products,
  tabName,
  firstRowRef,
  isLoading = false,
  selectedColorByProductKey = {},
  onProductClick,
  onProductColorChange,
  onProductExposure,
}: StyleProductListWapProps) {
  const productList = useMemo(() => normalizeProductList(products), [products]);
  const firstProduct = productList[0];
  const restProducts = productList.slice(1);

  if (isLoading) {
    return <StyleProductSkeletonListWap firstRowRef={firstRowRef} />;
  }

  if (productList.length === 0) {
    return null;
  }

  return (
    <div className="w-full px-[12px]">
      {firstProduct && (
        <div
          ref={firstRowRef}
          className="relative w-full"
          data-style-product-row-start={0}
          key={`${tabName}-${firstProduct.id || firstProduct.shop_sku}-0`}
        >
          <StyleProductCardWap
            product={firstProduct}
            variant="large"
            index={0}
            initialColorIndex={
              selectedColorByProductKey[getStyleHotProductColorStateKey(firstProduct)] || 0
            }
            onProductClick={onProductClick}
            onProductColorChange={onProductColorChange}
          />
          <ExposureDetector item={firstProduct} index={0} onExposure={onProductExposure} />
        </div>
      )}

      {restProducts.length > 0 && (
        <div className="mt-[8px] flex w-full flex-col gap-[8px]">
          {Array.from({ length: Math.ceil(restProducts.length / 2) }, (_, rowIndex) => {
            const rowProducts = restProducts.slice(rowIndex * 2, rowIndex * 2 + 2);
            const rowStartIndex = getStyleProductRowStartIndex(rowIndex * 2 + 1);

            return (
              <div
                className="grid w-full grid-cols-2 gap-x-[7px]"
                data-style-product-row-start={rowStartIndex}
                key={`${tabName}-wap-row-${rowStartIndex}`}
              >
                {rowProducts.map((product, index) => {
                  const productIndex = rowStartIndex + index;

                  return (
                    <div
                      key={`${tabName}-${product.id || product.shop_sku}-${productIndex}`}
                      className="relative"
                    >
                      <StyleProductCardWap
                        product={product}
                        variant="small"
                        index={productIndex}
                        initialColorIndex={
                          selectedColorByProductKey[getStyleHotProductColorStateKey(product)] || 0
                        }
                        onProductClick={onProductClick}
                        onProductColorChange={onProductColorChange}
                      />
                      <ExposureDetector
                        item={product}
                        index={productIndex}
                        onExposure={onProductExposure}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
